import { FC, ReactNode, useEffect, useState } from 'react';
import { Modal } from '@fe-learner-common';
import { TW_ROOT } from '@utils/constants';
import * as Dialog from '@radix-ui/react-dialog';
import { useIntl } from 'react-intl';
import { LoginAction } from './LoginAction';
import { useCreateAccount } from '@routes/selfreg/hooks/useCreateAccount';
import { FeedbackMessage } from '@fe-learner-common/components/FeedbackMessage';
import { useError } from '@hooks/useError';
import { BaseError } from '@fe-core';
import { FormValues, CreateAccountForm } from './CreateAccountForm';

type CreateAccountProps = {
  sessionId: string;
};
type States = 'login' | 'createAccount' | 'ok';

const CreateAccountButton = () => {
  const intl = useIntl();
  return (
    <Dialog.Trigger asChild>
      <button className="lf:l-btn lf:l-btn-md lf:l-btn-secondary lf:whitespace-pre">
        {intl.formatMessage({ id: 'catalog.selfreg.account.create.title' })}
      </button>
    </Dialog.Trigger>
  );
};

export const CreateAccount: FC<CreateAccountProps> = ({ sessionId }) => {
  const intl = useIntl();
  const [state, setState] = useState<States>('createAccount');
  const emitError = useError();

  const {
    mutate: createAccount,
    isPending,
    data: createAccountResponse,
  } = useCreateAccount({ sessionId });

  useEffect(() => {
    if (!createAccountResponse) return;

    if (createAccountResponse.status === 'unknown') {
      emitError({
        error: new BaseError(
          'Unknown error',
          intl.formatMessage({
            id: 'learner.common.error.unknown',
          }),
          intl.formatMessage({
            id: 'learner.common.error.unknown.description',
          }),
        ),
        errorMapOverride: {
          type: 'modal',
        },
      });
      return;
    }

    setState(createAccountResponse.status);
  }, [createAccountResponse, emitError, intl, setState]);

  const onSubmit = (data: FormValues) => {
    createAccount({
      email: data.email,
      redirect: window.location.href,
    });
  };

  const stateMap: Record<
    States,
    { title?: string; content: ReactNode; actions: ReactNode }
  > = {
    login: {
      content: (
        <FeedbackMessage
          status="info"
          texts={{
            title: intl.formatMessage({
              id: 'catalog.selfreg.account.create.login.title',
            }),
            description: intl.formatMessage({
              id: 'catalog.selfreg.account.create.login.description',
            }),
          }}
        />
      ),
      actions: <LoginAction />,
    },
    ok: {
      content: (
        <FeedbackMessage
          status="positive"
          texts={{
            title: intl.formatMessage({
              id: 'catalog.selfreg.account.create.ok.title',
            }),
            description: intl.formatMessage({
              id: 'catalog.selfreg.account.create.ok.description',
            }),
          }}
        />
      ),
      actions: (
        <Dialog.Close className="lf:l-btn lf:l-btn-md lf:l-btn-primary">
          {intl.formatMessage({ id: 'learner.common.close' })}
        </Dialog.Close>
      ),
    },
    createAccount: {
      title: intl.formatMessage({
        id: 'catalog.selfreg.account.create.title',
      }),
      content: <CreateAccountForm onSubmit={onSubmit} isLoading={isPending} />,
      actions: null,
    },
  };

  const { title, content, actions } = stateMap[state];

  return (
    <Modal
      Trigger={CreateAccountButton}
      className="lf:md:w-[640px]"
      TitleContent={<h2 className="lf:text-label-lg">{title}</h2>}
      actions={actions}
      container={document.getElementById(TW_ROOT) as HTMLElement}
      onClose={() => setState('createAccount')}
    >
      <div className="lf:px-6 lf:py-4">{content}</div>
    </Modal>
  );
};
